import axios from 'axios';
import API from '../config/API';

// Multi-location — thin client for /api/v2/sites/*. v2 reads the custom
// accessToken header (same pattern as user.service setFirstPassword).
function headers() {
    return { 'Content-Type': 'application/json', accessToken: localStorage.getItem('accessToken') || '' };
}
const get = (path, params) => axios.get(API.SITES + path, { headers: headers(), params });
const post = (path, body) => axios.post(API.SITES + path, body || {}, { headers: headers() });
const del = (path, body) => axios.delete(API.SITES + path, { headers: headers(), data: body || {} });
const put = (path, body) => axios.put(API.SITES + path, body || {}, { headers: headers() });
const data = (p) => p.then((r) => {
    const payload = r && r.data;
    if (!payload) return payload;
    // v2 reports API errors as HTTP 200 { statusCode: 0, error: { responseMessage } }.
    // Reject them so callers' catch blocks run — otherwise a "Transfer sent."
    // or "1 draft created" can show for a request the server refused.
    if (payload.statusCode === 0 && payload.error) {
        const msg = (payload.error && (payload.error.responseMessage || payload.error.message)) || 'Request failed';
        const err = new Error(msg);
        err.response = { data: { ...payload, message: msg } };
        err.data = payload.error.data || null;
        // The v2 error code (402, 409, 503…) and any extras the server set
        // (add-location: mode, intentStatus, needsBilling, intentId).
        err.code = payload.error.errorCode || null;
        err.info = payload.error;
        throw err;
    }
    // v2 responseHandler wraps successful results in responseData. Keep the
    // direct/data fallbacks for endpoints and tests that return an unwrapped
    // payload, but never leak the transport envelope into useSites().
    if (payload.responseData !== undefined) return payload.responseData;
    if (payload.data !== undefined) return payload.data;
    return payload;
});

export const sitesService = {
    mine: () => data(get('mine')),
    // Where each location is paid (separate bank per location). Owner only.
    payoutAccounts: (salonId) => data(get('payout-accounts', salonId ? { salonId } : undefined)),
    // Opens this location's own Stripe account (pending until Stripe has the bank).
    separatePayoutAccount: (salonId) => data(post('payout-accounts/separate', salonId ? { salonId } : {})),
    // Stripe-hosted set-up of the pending account → { url }. returnPath: a path on this site.
    payoutOnboardingLink: (returnPath, salonId) => data(post('payout-accounts/onboarding-link', { returnPath, ...(salonId ? { salonId } : {}) })),
    // Pay this location into another of the owner's accounts ('own' = the original).
    usePayoutAccount: (payoutAccountId, salonId) => data(post('payout-accounts/use', { payoutAccountId, ...(salonId ? { salonId } : {}) })),
    setActive: (salonId) => data(post('active', { salonId })),
    addLocation: (body) => data(post('add-location', body)),
    closeLocationPreview: (salonId) => data(get('close/preview', { salonId })),
    closeLocation: (salonId) => data(post('close', { salonId })),
    staffSites: (userId) => data(get('staff-sites', { userId })),
    addStaffToSite: (body) => data(post('staff/add', body)),
    // Hours per location: a person's rota AT a named site, and save it there.
    staffRota: (userId, salonId) => data(get('staff/rota', { userId, salonId })),
    saveStaffRota: (body) => data(put('staff/rota', body)),
    staffLeaveSite: (userId, salonId) => data(post('staff/leave', { userId, salonId })),
    // Changes nothing: { futureBookings } they still hold at that site, for the confirm.
    staffLeaveSitePreview: (userId, salonId) => data(post('staff/leave', { userId, salonId, preview: true })),
    // Everyone at the given sites (comma list, the Location picker's selection), or across the group.
    groupStaff: (salonIds) => data(get('staff', salonIds ? { salonIds } : undefined)),
    members: () => data(get('members')),
    upsertMember: (body) => data(post('members', body)),
    removeMember: (userId) => data(del('members', { userId })),
    updateSettings: (settings) => data(post('settings', settings)),
    dashboard: (month) => data(get('dashboard', { month })),
    // Phase 2
    crossSiteVouchers: (month) => data(get('vouchers/cross-site', { month })),
    clientAlsoVisits: (userId) => data(get('clients/also-visits', { userId })),
    clientSearch: (q, limit) => data(get('clients/search', { q, limit })),
    clientNotes: (userId) => data(get('clients/notes', { userId })),
    // schedule (optional): { sendNow } sends/schedules each copy at its own
    // site; absent = drafts only. Idempotent per site on the server.
    broadcastCampaign: (campaignId, salonIds, schedule) => data(post('campaigns/broadcast', schedule ? { campaignId, salonIds, schedule } : { campaignId, salonIds })),
    setBrandSlug: (slug) => data(post('brand/slug', { slug })),
    brandPage: (slug) => data(get(`brand/${encodeURIComponent(slug)}`)),
    serviceLinks: (commonServiceId) => data(get('catalogue/links', { commonServiceId })),
    // "Locations" control: make a service/product/voucher/category exist at chosen sites and match this one.
    syncCatalogue: (type, id, salonIds) => data(post('catalogue/sync', { type, id, salonIds })),
    unlinkService: (commonServiceId) => data(post('catalogue/unlink', { commonServiceId })),
    resetServiceToBrand: (commonServiceId) => data(post('catalogue/reset-to-brand', { commonServiceId })),
    pushSettings: (areas, salonIds) => data(post('settings/push', { areas, salonIds })),
    settingsAreas: () => data(get('settings/areas')),
    // Phase 3
    stockTransfers: (params) => data(get('stock/transfers', params)),
    stockTransfer: (id) => data(get(`stock/transfers/${id}`)),
    createStockTransfer: (body) => data(post('stock/transfers', body)),
    receiveStockTransfer: (id, received) => data(post(`stock/transfers/${id}/receive`, { received })),
    cancelStockTransfer: (id) => data(post(`stock/transfers/${id}/cancel`)),
    otherSiteBlocks: (staffUserIds, startDate, endDate) => data(get('diary/other-sites', { staffUserIds: staffUserIds.join(','), startDate, endDate })),
    myBookingsAllSites: (startDate, endDate) => data(get('diary/mine', { startDate, endDate })),
    // Add a location, billed first (docs/add-location-billing.md in beu-node-v2).
    // The server prices it (the salon plan, never a price id from here) and
    // keeps the flow as a "location intent", so it resumes on any device.
    locationQuote: (interval) => data(get('location-quote', interval ? { interval } : undefined)),
    // The open intent (null when there isn't one). One per target: a new
    // location, or ?existingSalonId= for putting that site on the plan. It also
    // finishes a paid checkout whose return and webhook were both lost.
    currentLocationIntent: (existingSalonId) => data(get('location-intents/current', existingSalonId ? { existingSalonId } : undefined)).then(pickIntent),
    // One intent by id, open or not (the "Setting up…" poll after Stripe).
    getLocationIntent: (id) => data(get(`location-intents/${encodeURIComponent(id)}`)).then(pickIntent),
    // { answers, step } for a new location, or { existingSalonId } to bill a site that isn't on the plan yet.
    saveLocationIntent: (body) => data(post('location-intents', body)).then(pickIntent),
    confirmLocationIntent: (id) => data(post(`location-intents/${encodeURIComponent(id)}/confirm`)).then(pickResult),
    // { url } to send them to Stripe, or { url: null, completed: true, intent, salon, billing }
    // when an earlier payment for this intent had already gone through.
    checkoutLocationIntent: (id, interval) => data(post(`location-intents/${encodeURIComponent(id)}/checkout`, { interval })),
    completeLocationIntent: (id, sessionId) => data(post(`location-intents/${encodeURIComponent(id)}/complete`, { sessionId })).then(pickResult),
    cancelLocationIntent: (id) => data(post(`location-intents/${encodeURIComponent(id)}/cancel`)).then(pickIntent),
    // "Sort payment" for a location whose own plan lapsed: { url } (Stripe's
    // page for the unpaid invoice), { needsNewPlan } (run the plan step for
    // that location), or { ok } (it is paid up again).
    sortLocationPayment: (locationId) => data(post('location-billing/sort-payment', { locationId })),
};

// An intent whether the server sends it bare or as { intent }. null stays null.
function pickIntent(r) {
    if (!r) return null;
    if (r.intent !== undefined) return r.intent || null;
    return r._id || r.id ? r : null;
}
// confirm / complete: { intent, salon, billing }, tolerating a bare intent.
function pickResult(r) {
    if (!r) return { intent: null, salon: null, billing: null };
    if (r.intent !== undefined) return { intent: r.intent || null, salon: r.salon || null, billing: r.billing || null };
    return { intent: pickIntent(r), salon: null, billing: null };
}

// Pages about ONE record (a booking, a client, a cash-up...) belong to the
// site you were on: after a switch, that section's list at the new site.
// Anything not listed here goes to the new site's diary.
const RECORD_SECTIONS = {
    clients: '/clients',
    staff: '/staff',
    cashup: '/cashup',
    stocktake: '/stocktake',
    transactions: '/transactions',
    forms: '/forms',
    'edit-services': '/services',
};
const OBJECT_ID = /^[a-f0-9]{24}$/i;

/**
 * Where a location switch should land: the same page at the new site. The old
 * site's id in the path (/diary/:salonId, /leave/:salonId...) becomes the new
 * one. A page about one of the OLD site's records has nothing to show at the
 * new site, so it lands on that section's list (or the diary). A query that
 * names a record (?c=<conversation id>) is dropped; any other query is kept.
 */
export function samePageAt({ pathname, search = '', hash = '' }, fromSalonId, toSalonId, userId) {
    const from = String(fromSalonId || '');
    const to = String(toSalonId || '');
    const me = String(userId || '');
    const diary = `/diary/${to}`;
    const segs = String(pathname || '/').split('/').map((seg) => (from && seg === from ? to : seg));
    const recordAt = segs.findIndex((seg) => OBJECT_ID.test(seg) && seg !== to && seg !== me);
    if (recordAt !== -1) return RECORD_SECTIONS[segs[1]] || diary;
    const path = segs.join('/') || '/';
    if (path === '/' || /^\/(login|signin)\b/i.test(path)) return diary;
    const query = /[a-f0-9]{24}/i.test(search) ? '' : search;
    return `${path}${query}${hash}`;
}

/**
 * Switch THIS device to a site: the server pins the token, then every
 * per-site cache in localStorage is dropped and the app reloads on `path`
 * (samePageAt: the page you were on) or, without one, the new site's diary.
 * One reload, not 109 edits (spec §7 step 4, §13b).
 */
// The header Location picker's selection (multiLocation/viewingSites.js) and
// the per-page keys it replaced.
export const VIEWING_KEYS = ['loc:viewing', 'fin:salonIds', 'mkt:salonIds'];

export async function switchToSite(salonId, path) {
    const res = await sitesService.setActive(salonId);
    // The combine pages' location selection belongs to the old site (else
    // Finance would show the old site's figures under the new one). Same
    // keys as viewingSites.clearViewing; inline because that module imports
    // this one, and the reload below resets its in-memory copy anyway.
    VIEWING_KEYS.forEach(k => { try { sessionStorage.removeItem(k); } catch (e) { /* ignore */ } });
    ['receptionModeSettings', 'cardBookingDate', 'diaryStaffOrder', 'salonTimezone', 'isShow', 'subscriptionGate', 'ml:sites', 'beu-access'].forEach(k => { try { localStorage.removeItem(k); } catch (e) { /* ignore */ } });
    try { sessionStorage.removeItem('ml:sites'); } catch (e) { /* ignore */ }
    localStorage.setItem('salonId', String(salonId));
    // Optional destination (the page you were on, or e.g. the new site's opening hours); default is its diary.
    window.location.href = path && path.startsWith('/') ? path : `/diary/${salonId}`;
    return res;
}

// Location colour: stable per site id, so the same dot appears everywhere.
const PALETTE = ['#2F6FED', '#12B76A', '#F79009', '#EE46BC', '#7A5AF8', '#06AED4', '#F04438', '#84CAFF', '#B692F6', '#FDB022'];
export function siteColour(salonId) {
    const s = String(salonId || '');
    let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return PALETTE[h % PALETTE.length];
}
export function siteShortName(name) {
    const n = String(name || '').trim();
    if (n.length <= 14) return n;
    const parts = n.split(/\s+/);
    return parts.length > 1 ? parts[parts.length - 1].slice(0, 14) : n.slice(0, 14);
}
